import { Controller } from "@hotwired/stimulus"

// Manages a single duplicate-group card on the Review Duplicates page.
// Handles: bulk-applying an action to all rows, hiding/showing the box-set
// picker when "Add to box set" is chosen, and tracking which book is the
// primary so "Mark as copy" applies to the right target.
export default class extends Controller {
  static targets = ["row", "boxSetPicker", "actions"]
  static values = { primary: Number }

  connect() {
    // Disable any "Mark as copy" radio on whichever row is currently primary —
    // a book can't be a copy of itself.
    this.refreshPrimaryUi()
  }

  // Bulk action — clicked from the chips at the top of the group.
  applyAll(event) {
    const action = event.currentTarget.dataset.actionValue
    this.rowTargets.forEach((row) => {
      // Skip the primary row for link/box_set/delete: the primary stays as-is.
      const isPrimary = parseInt(row.dataset.bookId, 10) === this.primaryValue
      if (isPrimary && (action === "link" || action === "delete")) return
      this.setRowAction(row, action)
    })
  }

  // Fired when the user picks a different primary radio in this group.
  setPrimary(event) {
    const newPrimary = parseInt(event.target.value, 10)
    this.primaryValue = newPrimary
    this.refreshPrimaryUi()
  }

  // Fired when the user changes a row's action radio. Show/hide the box-set
  // picker for that row.
  actionChanged(event) {
    const row = event.target.closest('[data-duplicate-group-target="row"]')
    if (!row) return
    this.refreshRowUi(row)
  }

  // ---- helpers ----

  setRowAction(row, action) {
    const radio = row.querySelector(`input[type="radio"][name^="book_action"][value="${action}"]`)
    if (radio) {
      radio.checked = true
      this.refreshRowUi(row)
    }
  }

  refreshRowUi(row) {
    const checked = row.querySelector('input[type="radio"][name^="book_action"]:checked')
    const action = checked && checked.value
    const picker = row.querySelector('[data-duplicate-group-target="boxSetPicker"]')
    if (picker) {
      picker.classList.toggle("hidden", action !== "box_set")
    }
  }

  refreshPrimaryUi() {
    this.rowTargets.forEach((row) => {
      const isPrimary = parseInt(row.dataset.bookId, 10) === this.primaryValue
      const linkRadio = row.querySelector('input[type="radio"][name^="book_action"][value="link"]')
      const deleteRadio = row.querySelector('input[type="radio"][name^="book_action"][value="delete"]')
      // The primary row can't link to itself or be deleted while it's the primary.
      ;[linkRadio, deleteRadio].forEach((r) => {
        if (!r) return
        const label = r.closest("label")
        if (isPrimary) {
          r.disabled = true
          if (r.checked) {
            // Reset to "keep" since the action is no longer valid for this row.
            const keep = row.querySelector('input[type="radio"][name^="book_action"][value="keep"]')
            if (keep) keep.checked = true
            this.refreshRowUi(row)
          }
          if (label) label.classList.add("opacity-50", "cursor-not-allowed")
        } else {
          r.disabled = false
          if (label) label.classList.remove("opacity-50", "cursor-not-allowed")
        }
      })

      // Also visually mark the primary row.
      row.classList.toggle("ring-2", isPrimary)
      row.classList.toggle("ring-amber-400", isPrimary)
      row.classList.toggle("border-amber-300", isPrimary)
    })
  }
}
